import { datadogRum } from "@datadog/browser-rum";
import { datadogLogs } from "@datadog/browser-logs";
import { reactPlugin } from "@datadog/browser-rum-react";

import type { DatadogConfig } from "./datadog-types";
import type {
  RumBeforeSend,
  RumFetchResourceEventDomainContext,
  RumXhrResourceEventDomainContext,
} from "@datadog/browser-rum";

const TRACE_ID_HEADER = "X-Trace-Id";
const CORRELATED_STATUS_THRESHOLD = 500;

// The SDK sets its session cookie from JavaScript, which can never carry
// HttpOnly, so security scans flag it. localStorage keeps the session off the
// wire entirely. RUM and Logs must share the same value, or each tracks its
// own session.
const SESSION_PERSISTENCE = "local-storage";

/**
 * Reads the backend's trace-ID response header (set by the Go services'
 * TraceIDHeaderMiddleware / TraceIDHeaderGinMiddleware) off a failed RUM
 * resource event, and logs it so the failure links to the backend APM trace
 * that produced it.
 *
 * Logged through `datadogLogs` rather than the shared `logger`: Datadog only
 * links a log to a trace when the ID arrives as a top-level `trace_id`, and
 * `logger` nests everything it is given under `data`.
 */
export const correlateFailedResourcesWithTrace: RumBeforeSend = (
  event,
  context,
) => {
  if (
    event.type !== "resource" ||
    (event.resource.status_code ?? 0) < CORRELATED_STATUS_THRESHOLD
  ) {
    return true;
  }

  const traceId =
    (context as RumXhrResourceEventDomainContext).xhr?.getResponseHeader(
      TRACE_ID_HEADER,
    ) ??
    (context as RumFetchResourceEventDomainContext).response?.headers.get(
      TRACE_ID_HEADER,
    );

  if (!traceId) {
    return true;
  }

  datadogLogs.logger.error(`Backend request failed: ${event.resource.url}`, {
    trace_id: traceId,
  });

  return true;
};

/**
 * Initializes Datadog RUM (Real User Monitoring) and/or Logs with the provided configuration.
 * This should be called once during application startup, typically in your main entry file.
 *
 * By default, both RUM and Logs are enabled. You can disable either by setting
 * `enableRum: false` or `enableLogs: false` in the config.
 *
 * @param config - Datadog configuration object
 * @example
 * ```typescript
 * // Enable both RUM and Logs (default)
 * initDatadog({
 *   env: 'production',
 *   applicationId: 'your-app-id',
 *   clientToken: 'your-client-token',
 *   site: 'datadoghq.com',
 *   service: 'my-app',
 *   version: '1.0.0',
 * });
 *
 * // Enable only Logs (no RUM)
 * initDatadog({
 *   env: 'production',
 *   clientToken: 'your-client-token',
 *   site: 'datadoghq.com',
 *   service: 'my-app',
 *   version: '1.0.0',
 *   enableRum: false,
 *   enableLogs: true,
 * });
 * ```
 */
export function initDatadog(config: DatadogConfig): void {
  const enableRum = config.enableRum !== false; // Default to true unless explicitly false
  const enableLogs = config.enableLogs !== false; // Default to true unless explicitly false

  if (enableRum) {
    if (!config.applicationId) {
      throw new Error(
        "applicationId is required when enableRum is true or undefined",
      );
    }

    datadogRum.init({
      env: config.env,
      applicationId: config.applicationId,
      clientToken: config.clientToken,
      site: config.site,
      service: config.service,
      version: config.version,
      sessionSampleRate: config.sessionSampleRate ?? 100,
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
      allowedTracingUrls: config.allowedTracingUrls ?? [
        /https:\/\/.*\.centrobenefitsgroup\.com/,
      ],
      defaultPrivacyLevel: "allow",
      traceSampleRate: config.traceSampleRate ?? 100,
      sessionReplaySampleRate: config.sessionReplaySampleRate ?? 100,
      plugins: [reactPlugin()],
      beforeSend: correlateFailedResourcesWithTrace,
      sessionPersistence: SESSION_PERSISTENCE,
    });
  }

  if (enableLogs) {
    datadogLogs.init({
      env: config.env,
      clientToken: config.clientToken,
      site: config.site,
      service: config.service,
      version: config.version,
      forwardErrorsToLogs: true,
      forwardConsoleLogs: "all",
      sessionSampleRate: config.sessionSampleRate ?? 100,
      sessionPersistence: SESSION_PERSISTENCE,
    });
  }
}
